import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast, Bounce } from "react-toastify";
import "./interviste.css";
import CustomSelect from "../CustomSelect";
import useTrainerSearch from "../hooks/useTrainerSearch";

const AddIntervista = () => {
  const [formData, setFormData] = useState({
    dataIntervista: "",
    trainerCodiceFiscale: "",
    parlantina: "",
    timidezza: "",
    dinamicità: "",
    empatia: "",
    inglese: "",
    adeguatoInsegnamento: "",
    automunito: "",
    piva: "",
    luogoLavorativoDesiderato: "",
    annotazioneInterna: "",
  });

  const [loading, setLoading] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const loadTrainerOptions = useTrainerSearch();

  const yesNoOptions = [
    { value: true, label: "Si" },
    { value: false, label: "No" },
  ];

  const enumOptions = {
    empatia: [
      { value: "Bassa", label: "Bassa" },
      { value: "Medio-bassa", label: "Medio-bassa" },
      { value: "Media", label: "Media" },
      { value: "Medio-alta", label: "Medio-alta" },
      { value: "Alta", label: "Alta" },
    ],
    inglese: [
      { value: "Basso", label: "Basso" },
      { value: "Medio", label: "Medio" },
      { value: "Alto", label: "Alto" },
    ],
    adeguatoInsegnamento: [
      { value: "Non adatto", label: "Non adatto" },
      { value: "Basso", label: "Basso" },
      { value: "Medio-basso", label: "Medio-basso" },
      { value: "Medio", label: "Medio" },
      { value: "Medio-alto", label: "Medio-alto" },
      { value: "Alto", label: "Alto" },
    ],
  };

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per modificare intervista!");
      navigate("/login");
      localStorage.clear();
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  const isValid = () => {
    const errors = {};

    if (!formData.trainerCodiceFiscale) {
      errors.trainerCodiceFiscale = "Seleziona un docente!";
    }

    if (!formData.dataIntervista) {
      errors.dataIntervista = "La data dell'intervista è obbligatoria!";
    }

    if (!formData.empatia) {
      errors.empatia = "Seleziona un livello di empatia!";
    }

    if (!formData.inglese) {
      errors.inglese = "Seleziona un livello di inglese!";
    }

    if (!formData.adeguatoInsegnamento) {
      errors.adeguatoInsegnamento = "Seleziona adeguatezza all'insegnamento!";
    }

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (inputErrors[field]) {
      setInputErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        dataIntervista: new Date(formData.dataIntervista).toISOString(),
      };

      const response = await api.post("/interviste", payload, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if ([200, 201].includes(response.status)) {
        toast.success("Intervista creata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        navigate("/interviste");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Qualcosa è andato storto!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) return null;

  const EnumField = ({
    field,
    label,
    options,
    required = false,
    className = "",
  }) => (
    <div className={`form-field ${className}`}>
      <label className="form-label">
        <i className="fa-solid fa-list"></i>
        {label}
        {required && <span className="required">*</span>}
      </label>
      <div className="form-input">
        <CustomSelect
          options={options}
          value={options.find((opt) => opt.value === formData[field]) || null}
          onChange={(selected) => handleChange(field, selected == null ? "" : selected.value)}
          isClearable={!required}
          placeholder={`Seleziona ${label.toLowerCase()}`}
          className="modern-select"
        />
        {inputErrors[field] && (
          <span className="error-message">
            <i className="fa-solid fa-exclamation-circle"></i>
            {inputErrors[field]}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">
          <i className="fa-solid fa-microphone"></i>
          Aggiungi Intervista
        </div>
        <div className="controls">
          <button onClick={() => navigate("/interviste")} className="back-btn">
            <i className="fa-solid fa-arrow-left"></i>
            Torna alle Interviste
          </button>
        </div>
      </div>

      <div className="page-content add-intervista">
        <div className="form-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fa-solid fa-clipboard-list"></i>
              Dettagli Intervista
            </div>
            <div className="card-subtitle">
              Compila i campi per registrare una nuova intervista con un docente
            </div>
          </div>

          <div className="card-content">
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-user-tie"></i>
                  Docente
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <CustomSelect
                    loadOptions={loadTrainerOptions}
                    value={selectedTrainer}
                    onChange={(selected) => {
                      setSelectedTrainer(selected);
                      handleChange(
                        "trainerCodiceFiscale",
                        selected?.trainer?.codiceFiscale ||
                          selected?.value ||
                          ""
                      );
                    }}
                    placeholder="Seleziona un docente"
                    className="modern-select"
                  />
                  {inputErrors.trainerCodiceFiscale && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.trainerCodiceFiscale}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-calendar-alt"></i>
                  Data Intervista
                  <span className="required">*</span>
                </label>
                <div className="form-input">
                  <input
                    type="date"
                    value={formData.dataIntervista}
                    onChange={(e) =>
                      handleChange("dataIntervista", e.target.value)
                    }
                    className="modern-input"
                  />
                  {inputErrors.dataIntervista && (
                    <span className="error-message">
                      <i className="fa-solid fa-exclamation-circle"></i>
                      {inputErrors.dataIntervista}
                    </span>
                  )}
                </div>
              </div>

              <EnumField
                field="parlantina"
                label="Parlantina"
                options={yesNoOptions}
              />

              <EnumField
                field="timidezza"
                label="Timidezza"
                options={yesNoOptions}
              />

              <EnumField
                field="dinamicità"
                label="Dinamicità"
                options={yesNoOptions}
              />

              <EnumField
                field="automunito"
                label="Automunito"
                options={yesNoOptions}
              />

              <EnumField field="piva" label="P.IVA" options={yesNoOptions} />

              <EnumField
                field="empatia"
                label="Empatia"
                options={enumOptions.empatia}
                required
              />

              <EnumField
                field="inglese"
                label="Livello Inglese"
                options={enumOptions.inglese}
                required
              />

              <EnumField
                field="adeguatoInsegnamento"
                label="Adeguatezza Insegnamento"
                options={enumOptions.adeguatoInsegnamento}
                required
              />

              <div className="form-field">
                <label className="form-label">
                  <i className="fa-solid fa-map-marker-alt"></i>
                  Luogo Lavorativo Desiderato
                </label>
                <div className="form-input">
                  <input
                    type="text"
                    value={formData.luogoLavorativoDesiderato}
                    onChange={(e) =>
                      handleChange("luogoLavorativoDesiderato", e.target.value)
                    }
                    placeholder="Inserisci luogo lavorativo desiderato"
                    className="modern-input"
                  />
                </div>
              </div>

              <div className="form-field full-width">
                <label className="form-label">
                  <i className="fa-solid fa-sticky-note"></i>
                  Annotazione Interna
                </label>
                <div className="form-input">
                  <textarea
                    value={formData.annotazioneInterna}
                    onChange={(e) =>
                      handleChange("annotazioneInterna", e.target.value)
                    }
                    placeholder="Inserisci annotazioni interne"
                    className="modern-textarea"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => navigate("/interviste")}
              >
                <i className="fa-solid fa-times"></i>
                Annulla
              </button>
              <button className="btn-primary" onClick={handleSubmit}>
                <i className="fa-solid fa-save"></i>
                Crea Intervista
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddIntervista;
